import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { useApi } from "../../hooks/useApi";
import { useBrandId } from "../../hooks/useBrandId";
import { useCompanyId } from "../../hooks/useCompanyId";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  MenuItem,
  Collapse,
  IconButton,
  TextField,
  Menu,
  Checkbox,
  FormControlLabel,
  Divider,
  Badge,
  TableSortLabel,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import * as Sentry from "@sentry/react";
import {
  OrderInsightMatchStatus,
  OrderInsightMatchStatusLabel,
  OrderInsightDeflectionOutcome,
  OrderInsightDeflectionOutcomeLabel,
  OrderInsightRow,
  OrderInsightMerchant,
  OrderInsightMerchantListResponse,
  OrderInsightListResponse,
  OrderLookupSource,
  OrderLookupSourceLabel,
} from "../../types/verifiOrderInsight";
import DeflectionChip from "../../components/alerts/DeflectionChip";
import { formatCurrency } from "../../utils/formatCurrency";
import MultiSelectFilter from "../../components/common/MultiSelectFilter";
import SearchableMultiSelectFilter from "../../components/common/SearchableMultiSelectFilter";
import { filterPanelSx } from "../../components/common/filterStyles";

const COLUMNS_STORAGE_KEY = 'order_insight_visible_columns_v1';

interface ColumnConfig {
  id: string;
  label: string;
  isDefault: boolean;
}

const allColumns: ColumnConfig[] = [
  { id: 'insight_id', label: 'Insight ID', isDefault: false },
  { id: 'source', label: 'Source', isDefault: true },
  { id: 'company_name', label: 'Company', isDefault: false },
  { id: 'merchant_name', label: 'Merchant', isDefault: true },
  { id: 'payment_descriptor', label: 'Descriptor', isDefault: true },
  { id: 'acquirer_bin', label: 'Acquirer BIN', isDefault: false },
  { id: 'card_acceptor_id', label: 'CAID', isDefault: false },
  { id: 'received_at', label: 'Received At', isDefault: true },
  { id: 'amount', label: 'Amount', isDefault: true },
  { id: 'card_number', label: 'Card Number', isDefault: true },
  { id: 'auth_code', label: 'Auth Code', isDefault: true },
  { id: 'transaction_id', label: 'Transaction ID', isDefault: false },
  { id: 'arn', label: 'ARN', isDefault: false },
  { id: 'transaction_date', label: 'Transaction Date', isDefault: false },
  { id: 'match_status', label: 'Match', isDefault: true },
  { id: 'deflection', label: 'Deflection', isDefault: true },
  { id: 'http_status', label: 'Response HTTP Status', isDefault: false },
  { id: 'latency_sec', label: 'Latency (sec)', isDefault: false },
  { id: 'warnings', label: 'Warnings', isDefault: false },
];

const defaultColumnIds = allColumns.filter(c => c.isDefault).map(c => c.id);
const validColumnIds = allColumns.map(c => c.id);

const getStoredColumns = (): string[] => {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.filter((id: string) => validColumnIds.includes(id));
      }
    }
  } catch (e) {
    console.error('Failed to parse stored columns', e);
  }
  return defaultColumnIds;
};

const saveStoredColumns = (columnIds: string[]) => {
  localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columnIds));
};

const formatDateTime = (dateString: string | Date | null | undefined) => {
  if (!dateString) return '--';
  const dateStr = dateString instanceof Date ? dateString.toISOString() : dateString;
  return dayjs.utc(dateStr).format('MMM DD, YYYY HH:mm');
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '--';
  return dayjs.utc(dateString).format('MMM DD, YYYY');
};

const formatCardNumber = (cardBin?: string | null, cardLast4?: string | null) => {
  if (!cardBin && !cardLast4) return '--';
  if (cardBin && cardLast4) return `${cardBin}****${cardLast4}`;
  if (cardLast4) return `****${cardLast4}`;
  return cardBin || '--';
};

const formatLatencySec = (latencyMs: number | null) => {
  if (latencyMs == null) return '--';
  return (latencyMs / 1000).toFixed(2);
};

const formatPpAccount = (ppType: string | null, ppAccountId: number | null) => {
  if (!ppType && !ppAccountId) return '--';
  if (ppType && ppAccountId) return `${ppType} / ${ppAccountId}`;
  return ppType ?? String(ppAccountId);
};

const MATCH_STATUS_COLOR: Record<OrderInsightMatchStatus, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  matched: 'success',
  not_found: 'warning',
  multiple_matches: 'warning',
  no_transaction_source: 'info',
  unmapped_merchant: 'error',
  missing_merchant_info: 'error',
  error: 'error',
};

function MatchStatusChip({ status }: { status: OrderInsightMatchStatus }) {
  return (
    <Chip
      label={OrderInsightMatchStatusLabel[status] ?? status}
      color={MATCH_STATUS_COLOR[status] ?? 'default'}
      size="small"
      variant="outlined"
    />
  );
}

const MATCH_STATUS_OPTIONS: { value: string; label: string }[] =
  Object.values(OrderInsightMatchStatus).map(value => ({
    value,
    label: OrderInsightMatchStatusLabel[value],
  }));

const SOURCE_OPTIONS: { value: string; label: string }[] =
  Object.values(OrderLookupSource).map(value => ({
    value,
    label: OrderLookupSourceLabel[value],
  }));

const DEFLECTION_OPTIONS: { value: string; label: string }[] =
  Object.values(OrderInsightDeflectionOutcome).map(value => ({
    value,
    label: OrderInsightDeflectionOutcomeLabel[value],
  }));

const validMatchStatusValues = MATCH_STATUS_OPTIONS.map(o => o.value);
const validSourceValues = SOURCE_OPTIONS.map(o => o.value);
const validDeflectionValues = DEFLECTION_OPTIONS.map(o => o.value);

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {typeof value === 'string' || typeof value === 'number'
        ? <Typography variant="body1" sx={{ fontWeight: 500 }}>{value}</Typography>
        : value}
    </Box>
  );
}

function renderCell(row: OrderInsightRow, columnId: string): React.ReactNode {
  switch (columnId) {
    case 'insight_id':
      return <TableCell key={columnId}>{row.insight_id}</TableCell>;
    case 'source':
      return <TableCell key={columnId}>{OrderLookupSourceLabel[row.source] ?? row.source}</TableCell>;
    case 'company_name':
      return <TableCell key={columnId}>{row.company_name || '--'}</TableCell>;
    case 'merchant_name':
      return <TableCell key={columnId}>{row.merchant_name || '--'}</TableCell>;
    case 'payment_descriptor':
      return <TableCell key={columnId}>{row.payment_descriptor || '--'}</TableCell>;
    case 'acquirer_bin':
      return <TableCell key={columnId}>{row.acquirer_bin || '--'}</TableCell>;
    case 'card_acceptor_id':
      return <TableCell key={columnId}>{row.card_acceptor_id || '--'}</TableCell>;
    case 'received_at':
      return <TableCell key={columnId} sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.received_at)}</TableCell>;
    case 'amount':
      return (
        <TableCell key={columnId}>
          {row.transaction_amount != null
            ? formatCurrency(row.transaction_amount, row.transaction_currency || 'USD', true)
            : '--'}
        </TableCell>
      );
    case 'card_number':
      return <TableCell key={columnId}>{formatCardNumber(row.card_bin, row.card_last4)}</TableCell>;
    case 'auth_code':
      return <TableCell key={columnId}>{row.auth_code || '--'}</TableCell>;
    case 'transaction_id':
      return <TableCell key={columnId}>{row.transaction_id || '--'}</TableCell>;
    case 'arn':
      return <TableCell key={columnId}>{row.arn || '--'}</TableCell>;
    case 'transaction_date':
      return <TableCell key={columnId} sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.transaction_date)}</TableCell>;
    case 'match_status':
      return <TableCell key={columnId}><MatchStatusChip status={row.match_status} /></TableCell>;
    case 'deflection':
      return <TableCell key={columnId}><DeflectionChip caseEvent={row.deflection_event} /></TableCell>;
    case 'http_status':
      return <TableCell key={columnId}>{row.http_status}</TableCell>;
    case 'latency_sec':
      return <TableCell key={columnId}>{formatLatencySec(row.latency_ms)}</TableCell>;
    case 'warnings': {
      const missingCompany = !row.company_id;
      const missingPp = !row.pp_account_id;
      if (!missingCompany && !missingPp) {
        return <TableCell key={columnId}></TableCell>;
      }
      return (
        <TableCell key={columnId}>
          <Typography variant="body2" sx={{ color: '#ed6c02', whiteSpace: 'nowrap' }}>
            {missingCompany ? 'No company' : 'No PP Account'}
          </Typography>
        </TableCell>
      );
    }
    default:
      return null;
  }
}

function OrderInsightRowItem({ row, visibleColumns }: { row: OrderInsightRow; visibleColumns: string[] }) {
  const [open, setOpen] = useState(false);
  const [payloadExpanded, setPayloadExpanded] = useState(false);

  return (
    <React.Fragment>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}>
        {allColumns.filter(c => visibleColumns.includes(c.id)).map(c => renderCell(row, c.id))}
        <TableCell />
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={visibleColumns.length + 1}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Insight Details
                </Typography>
                {row.is_staging && (
                  <Chip label="Staging" size="small" color="info" variant="outlined" />
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, maxWidth: 1200 }}>
                <DetailField label="Insight ID" value={row.insight_id} />
                {row.linked_insight_id && (
                  <DetailField label="Linked Insight ID" value={row.linked_insight_id} />
                )}
                <DetailField label="Source" value={OrderLookupSourceLabel[row.source] ?? row.source} />
                <DetailField label="Match" value={<MatchStatusChip status={row.match_status} />} />
                <DetailField label="HTTP Status" value={String(row.http_status)} />
                <DetailField label="Latency (sec)" value={formatLatencySec(row.latency_ms)} />
                <DetailField label="Transaction Request ID" value={row.transaction_request_id || '--'} />
                <DetailField label="Company" value={row.company_name || '--'} />
                <DetailField label="Merchant" value={row.merchant_name || '--'} />
                <DetailField label="PP Account" value={formatPpAccount(row.pp_type, row.pp_account_id)} />
                <DetailField label="Descriptor" value={row.payment_descriptor || '--'} />
                <DetailField label="Acquirer BIN" value={row.acquirer_bin || '--'} />
                <DetailField label="Card Acceptor ID" value={row.card_acceptor_id || '--'} />
                <DetailField
                  label="Amount"
                  value={
                    row.transaction_amount != null
                      ? formatCurrency(row.transaction_amount, row.transaction_currency || 'USD', true)
                      : '--'
                  }
                />
                <DetailField label="Card Number" value={formatCardNumber(row.card_bin, row.card_last4)} />
                <DetailField label="Auth Code" value={row.auth_code || '--'} />
                <DetailField label="Transaction ID" value={row.transaction_id || '--'} />
                <DetailField label="ARN" value={row.arn || '--'} />
                <DetailField label="Transaction Date" value={formatDate(row.transaction_date)} />
                <DetailField label="Received At" value={formatDateTime(row.received_at)} />
                {row.deflection_event && (
                  <>
                    <DetailField label="Deflection" value={<DeflectionChip caseEvent={row.deflection_event} />} />
                    <DetailField label="CE Case ID" value={row.deflection_case_id || '--'} />
                  </>
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setPayloadExpanded(!payloadExpanded)}>
                  <IconButton size="small" sx={{ padding: '4px' }}>
                    {payloadExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Response Payload
                  </Typography>
                </Box>
                <Collapse in={payloadExpanded} timeout="auto" unmountOnExit>
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 2,
                      bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'grey.900',
                      borderRadius: 1,
                      fontSize: '0.8125rem',
                      overflow: 'auto',
                      maxHeight: 400,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.response_payload
                      ? JSON.stringify(row.response_payload, null, 2)
                      : 'No response payload'}
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function OrderInsight() {
  const { get, loading, error: apiError } = useApi();
  const { get: getMerchants } = useApi();
  const { companyId } = useCompanyId();
  const { brandId } = useBrandId();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  const initialStartDate = urlSearchParams.get('startDate') ? dayjs.utc(urlSearchParams.get('startDate')) : dayjs.utc().subtract(7, 'day');
  const initialEndDate = urlSearchParams.get('endDate') ? dayjs.utc(urlSearchParams.get('endDate')) : dayjs.utc();
  const initialMatchStatuses = (urlSearchParams.get('matchStatuses') || '').split(',').filter(v => validMatchStatusValues.includes(v));
  const initialSources = (urlSearchParams.get('sources') || '').split(',').filter(v => validSourceValues.includes(v));
  const initialDeflections = (urlSearchParams.get('deflections') || '').split(',').filter(v => validDeflectionValues.includes(v));
  const initialInsightId = urlSearchParams.get('insightId') || '';
  const initialMerchantIds = (urlSearchParams.get('merchantIds') || '').split(',').filter(Boolean);
  const initialNoCompany = urlSearchParams.get('noCompany') === 'true';
  const initialNoPpAccount = urlSearchParams.get('noPpAccount') === 'true';
  const initialPage = urlSearchParams.get('page') ? Math.max(0, parseInt(urlSearchParams.get('page')!, 10)) : 0;
  const initialRowsPerPage = urlSearchParams.get('rowsPerPage') ? parseInt(urlSearchParams.get('rowsPerPage')!, 10) : 25;
  const initialSortOrder = (urlSearchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';

  const [startDate, setStartDate] = useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate);
  const [matchStatuses, setMatchStatuses] = useState<string[]>(initialMatchStatuses);
  const [sources, setSources] = useState<string[]>(initialSources);
  const [deflections, setDeflections] = useState<string[]>(initialDeflections);
  const [insightId, setInsightId] = useState<string>(initialInsightId);
  const [merchantIds, setMerchantIds] = useState<string[]>(initialMerchantIds);
  const [noCompany, setNoCompany] = useState(initialNoCompany);
  const [noPpAccount, setNoPpAccount] = useState(initialNoPpAccount);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);

  const [data, setData] = useState<OrderInsightListResponse | null>(null);
  const [merchants, setMerchants] = useState<OrderInsightMerchant[]>([]);
  const [pageError, setPageError] = useState<string | null>(null);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const columnMenuOpen = Boolean(columnMenuAnchor);

  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const hasActiveFilters = insightId !== '' || merchantIds.length > 0 || noCompany || noPpAccount;
  const [filtersExpanded, setFiltersExpanded] = useState(hasActiveFilters);
  const activeFiltersCount =
    (insightId !== '' ? 1 : 0) +
    (merchantIds.length > 0 ? 1 : 0) +
    (noCompany ? 1 : 0) +
    (noPpAccount ? 1 : 0);

  const merchantOptions = merchants.map(m => ({
    value: String(m.merchant_id),
    label: m.merchant_name || `Merchant ${m.merchant_id}`,
  }));

  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns(prev => {
      const next = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      saveStoredColumns(next);
      return next;
    });
  };

  const handleResetColumns = () => {
    setVisibleColumns(defaultColumnIds);
    saveStoredColumns(defaultColumnIds);
  };

  const handleShowAllColumns = () => {
    setVisibleColumns(validColumnIds);
    saveStoredColumns(validColumnIds);
  };

  const handleSortToggle = () => {
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(0);
  };

  useEffect(() => {
    const params = new URLSearchParams(urlSearchParams);

    if (startDate) params.set('startDate', startDate.format('YYYY-MM-DD'));
    else params.delete('startDate');

    if (endDate) params.set('endDate', endDate.format('YYYY-MM-DD'));
    else params.delete('endDate');

    if (matchStatuses.length) params.set('matchStatuses', matchStatuses.join(','));
    else params.delete('matchStatuses');

    if (sources.length) params.set('sources', sources.join(','));
    else params.delete('sources');

    if (deflections.length) params.set('deflections', deflections.join(','));
    else params.delete('deflections');

    if (insightId) params.set('insightId', insightId);
    else params.delete('insightId');

    if (merchantIds.length) params.set('merchantIds', merchantIds.join(','));
    else params.delete('merchantIds');

    if (noCompany) params.set('noCompany', 'true');
    else params.delete('noCompany');

    if (noPpAccount) params.set('noPpAccount', 'true');
    else params.delete('noPpAccount');

    if (page > 0) params.set('page', page.toString());
    else params.delete('page');

    if (rowsPerPage !== 25) params.set('rowsPerPage', rowsPerPage.toString());
    else params.delete('rowsPerPage');

    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder);
    else params.delete('sortOrder');

    setUrlSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, matchStatuses, sources, deflections, insightId, merchantIds, noCompany, noPpAccount, page, rowsPerPage, sortOrder]);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setPageError(null);

    try {
      const params = new URLSearchParams({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        sortOrder,
      });
      if (matchStatuses.length) params.set('matchStatuses', matchStatuses.join(','));
      if (sources.length) params.set('sources', sources.join(','));
      if (deflections.length) params.set('deflections', deflections.join(','));
      if (insightId) params.set('insightId', insightId);
      if (merchantIds.length) params.set('merchantIds', merchantIds.join(','));
      if (noCompany) params.set('noCompany', 'true');
      if (noPpAccount) params.set('noPpAccount', 'true');
      if (companyId) params.set('companyId', companyId.toString());
      if (brandId) params.set('brandId', String(brandId));

      const response = await get<OrderInsightListResponse>(`/api/order-insight?${params.toString()}`);
      setData(response);
    } catch (err) {
      console.error('Failed to fetch order insight:', err);
      setPageError(err instanceof Error ? err.message : 'Unknown error');
      Sentry.captureException(err);
      setData(null);
    }
  }, [get, startDate, endDate, matchStatuses, sources, deflections, insightId, merchantIds, noCompany, noPpAccount, companyId, brandId, page, rowsPerPage, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getMerchants<OrderInsightMerchantListResponse>('/api/order-insight/merchants');
        if (!cancelled) setMerchants(response.merchants);
      } catch (err) {
        console.error('Failed to load OI merchants:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [getMerchants]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Alerts - Order Insights"
        description="Chargemont | Alerts - Order Insights"
      />
      <PageContainer>
        <PageHeader title="Order Insights" />

        {/* Filters */}
        <Box
          className="mb-6"
          sx={filterPanelSx}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePicker
                    label="Start"
                    value={startDate}
                    onChange={(d) => { setStartDate(d); setPage(0); }}
                    open={startPickerOpen}
                    onOpen={() => setStartPickerOpen(true)}
                    onClose={() => setStartPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: "fit-content" },
                        onClick: () => setStartPickerOpen(true),
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>—</Typography>
                  <DatePicker
                    label="End"
                    value={endDate}
                    onChange={(d) => { setEndDate(d); setPage(0); }}
                    open={endPickerOpen}
                    onOpen={() => setEndPickerOpen(true)}
                    onClose={() => setEndPickerOpen(false)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { width: "fit-content" },
                        onClick: () => setEndPickerOpen(true),
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
              <MultiSelectFilter
                label="Source"
                options={SOURCE_OPTIONS}
                value={sources}
                width={180}
                onChange={(values) => { setSources(values); setPage(0); }}
              />

              <MultiSelectFilter
                label="Match"
                options={MATCH_STATUS_OPTIONS}
                value={matchStatuses}
                width={200}
                onChange={(values) => { setMatchStatuses(values); setPage(0); }}
              />

              <MultiSelectFilter
                label="Deflection"
                options={DEFLECTION_OPTIONS}
                value={deflections}
                width={160}
                onChange={(values) => { setDeflections(values); setPage(0); }}
              />

              <IconButton
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: filtersExpanded ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: filtersExpanded ? 'primary.50' : 'transparent',
                  '&:hover': {
                    backgroundColor: filtersExpanded ? 'primary.100' : 'action.hover',
                  },
                }}
              >
                <Badge
                  badgeContent={activeFiltersCount}
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                >
                  <FilterListIcon fontSize="small" sx={{ color: filtersExpanded ? 'primary.main' : 'text.secondary' }} />
                </Badge>
              </IconButton>
            </Box>
          </Box>

          <Collapse in={filtersExpanded}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
              sx={{ px: 2, pb: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <TextField
                size="small"
                label="Insight ID / CE Case ID"
                value={insightId}
                onChange={(e) => { setInsightId(e.target.value); setPage(0); }}
                sx={{ minWidth: 280 }}
                placeholder="Insight ID or CE Case ID"
              />
              <SearchableMultiSelectFilter
                label="Merchant"
                options={merchantOptions}
                value={merchantIds}
                width={240}
                searchPlaceholder="Search merchants…"
                showSelectAllClearAll
                onChange={(values) => { setMerchantIds(values); setPage(0); }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={noCompany} onChange={(e) => { setNoCompany(e.target.checked); setPage(0); }} />}
                label="Without company"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={noPpAccount} onChange={(e) => { setNoPpAccount(e.target.checked); setPage(0); }} />}
                label="Without PP Account"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            </Box>
          </Collapse>
        </Box>

        {(apiError || pageError) && (
          <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            Error: {apiError || pageError}
          </Box>
        )}

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {allColumns.filter(c => visibleColumns.includes(c.id)).map(column => (
                    <TableCell key={column.id} sx={{ whiteSpace: 'nowrap' }}>
                      {column.id === 'received_at' ? (
                        <TableSortLabel
                          active
                          direction={sortOrder}
                          onClick={handleSortToggle}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={handleColumnMenuOpen}
                      size="small"
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <SettingsOutlinedIcon fontSize="medium" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : data?.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      <Typography variant="body2" color="text.secondary">No order insight records found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.rows.map(row => (
                    <OrderInsightRowItem key={row.verifi_order_insight_id} row={row} visibleColumns={visibleColumns} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data && (
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={data.pagination.totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </PageContainer>

      <Menu
        anchorEl={columnMenuAnchor}
        open={columnMenuOpen}
        onClose={handleColumnMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { maxHeight: 400, width: 260 } },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Configure Columns</Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 1 }}>
          {allColumns.map((column) => (
            <MenuItem
              key={column.id}
              dense
              sx={{ py: 0.5 }}
              onClick={() => handleColumnToggle(column.id)}
            >
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                size="small"
                sx={{ py: 0, mr: 1 }}
              />
              <Typography variant="body2">{column.label}</Typography>
            </MenuItem>
          ))}
        </Box>
        <Divider />
        <MenuItem onClick={handleResetColumns} dense>
          <Typography variant="body2" color="primary">Default Columns</Typography>
        </MenuItem>
        <MenuItem onClick={handleShowAllColumns} dense>
          <Typography variant="body2" color="primary">All Columns</Typography>
        </MenuItem>
      </Menu>
    </div>
  );
}
